/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable import/prefer-default-export */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import { ReportProblemOutlined as ReportProblemOutlinedIcon } from '@material-ui/icons';
import { getUserId } from '@src/selectors/user';
import { getDayOfYear, startOfToday } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';

/**
 * Stores the day of the year when the email OTP reminder is set for the given agent id.
 * @param {number} id - The agent id.
 */
function setEmailOtpReminder(id: number) {
  try {
    window.localStorage.setItem(key(id), getDayOfYear(new Date()).toString());
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
}

/**
 * Retrieves the day of the year when the email OTP reminder is set for the given agent id.
 * @param {number} id - The agent id.
 * @returns {string|null} The day of the year when the email OTP reminder is set, or null if no reminder is set.
 */
function getEmailOtpReminder(id: number) {
  try {
    const dayOfYear = window.localStorage.getItem(key(id));
    if (dayOfYear !== null) return Number(dayOfYear);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
  return null;
}

export function removeEmailOtpReminder(id: number) {
  try {
    window.localStorage.removeItem(key(id));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
}

function key(id: number) {
  return `${id}:email-otp-reminder`;
}

const eventTarget = new EventTarget();

export function displayEmailOtpReminder(show = true) {
  eventTarget.dispatchEvent(
    new CustomEvent('email-otp-reminder', { detail: show })
  );
}

export function useEmailOtpReminder() {
  const agentId = useSelector(getUserId);
  const [open, setOpen] = useState(false);

  const shouldDisplay = useRef(false);
  useEffect(() => {
    const handler = (event: Event) => {
      shouldDisplay.current = (event as CustomEvent).detail;
    };
    eventTarget.addEventListener('email-otp-reminder', handler);
    return () => eventTarget.removeEventListener('email-otp-reminder', handler);
  }, []);

  const match = useRouteMatch(['/agent/*', '/admin/*']);

  useEffect(() => {
    if (match) {
      const lastReminder = getEmailOtpReminder(agentId);
      const DaysSinceLastReminder = 7;
      if (
        !lastReminder ||
        Math.abs(getDayOfYear(startOfToday()) - lastReminder) >=
          DaysSinceLastReminder
      ) {
        setTimeout(() => {
          if (shouldDisplay.current) {
            shouldDisplay.current = false;
            setOpen(true);
            setEmailOtpReminder(agentId);
          }
        }, 10_000);
      }
    }
  }, [match, agentId]);

  return [open, setOpen] as const;
}

const TwoFactorAuthEmailOtpReminder = () => {
  const history = useHistory();
  const [open, setOpen] = useEmailOtpReminder();

  return (
    <Dialog open={open}>
      <DialogTitle>
        <Box display="flex" alignItems="center" gridGap={8}>
          <FormattedMessage id="TwoFactorAuth.EmailOtpReminder.Title" />{' '}
          <Box component="span" display="flex" color="warning.main">
            <ReportProblemOutlinedIcon fontSize="small" color="inherit" />
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <FormattedMessage id="TwoFactorAuth.EmailOtpReminder.Description" />
        <Box fontWeight={700} mt={2}>
          <FormattedMessage id="TwoFactorAuth.EmailOtpReminder.Hint" />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setOpen(false)}
        >
          <FormattedMessage id="TwoFactorAuth.EmailOtpReminder.Later" />
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            history.push('/admin/profile-settings', { tab: 1 });
            setOpen(false);
          }}
        >
          <FormattedMessage id="TwoFactorAuth.EmailOtpReminder.SetupNow" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TwoFactorAuthEmailOtpReminder;
